import React, { useState } from "react";
import { Formik, Form } from "formik";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
} from "@mui/material";
import backgroundImg from "../../assets/background-img.jpeg";
import logo from "../../assets/Logo.jpg";
import {
  forgotPasswordEmailInitialValues,
  ForgotPasswordEmailSchema,
  otpInitialValues,
  OTPSchema,
  changePasswordInitialValues,
  ChangePasswordSchema,
} from "../../validation/account.validation";
import { sendResetEmail, verifyOTP, resetPassword } from "../../services/account.service";
import { useNavigate } from "react-router-dom";

const ForgotPasswordForm: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'otp' | 'password'>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSendResetEmail = async (values: { email: string }) => {
    try {
      await sendResetEmail(values.email);
      setEmail(values.email);
      setStep('otp');
      setSnackbar({
        open: true,
        message: "Reset email sent successfully!",
        severity: "success",
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.message || "Failed to send reset email",
        severity: "error",
      });
    }
  };

  const handleVerifyOTP = async (values: { otp: string }) => {
    try {
      await verifyOTP(email, values.otp);
      setOtp(values.otp);
      setStep('password');
      setSnackbar({
        open: true,
        message: "OTP verified successfully!",
        severity: "success",
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.message || "Invalid OTP",
        severity: "error",
      });
    }
  };

  const handleResetPassword = async (values: { newPassword: string; confirmPassword: string }) => {
    // Additional client-side validation
    if (values.newPassword !== values.confirmPassword) {
      setSnackbar({
        open: true,
        message: "Passwords do not match",
        severity: "error",
      });
      return;
    }

    try {
      await resetPassword(email, otp, values.newPassword);
      setSnackbar({
        open: true,
        message: "Password reset successfully!",
        severity: "success",
      });
      navigate("/login");
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.message || "Failed to reset password",
        severity: "error",
      });
    }
  };

  return (
    <Box
      sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
    >
      <Box
        flex={1}
        sx={{
          background: `url(${backgroundImg}) no-repeat center center`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 2,
          width: "100%",
          minHeight: "100vh",
          backgroundSize: "cover",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            maxWidth: 500,
            borderRadius: 4,
            p: { xs: 3, sm: 3 },
            mx: 2,
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.10)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <img
            src={logo}
            alt="Logo"
            style={{ width: 100, height: 100, objectFit: "contain" }}
          />
          <Typography
            variant="h6"
            fontWeight={700}
            align="center"
            sx={{ mb: 1, letterSpacing: 0.5 }}
          >
            {step === 'email' && 'Reset Password'}
            {step === 'otp' && 'Enter OTP'}
            {step === 'password' && 'New Password'}
          </Typography>
          <Typography
            align="center"
            sx={{ mb: 2, color: "text.secondary", fontSize: "0.975rem" }}
          >
            {step === 'email' && 'Enter your email to receive a reset code'}
            {step === 'otp' && `Enter the 4-digit code sent to ${email}`}
            {step === 'password' && 'Enter your new password'}
          </Typography>

          {step === 'email' && (
            <Formik
              initialValues={forgotPasswordEmailInitialValues}
              validationSchema={ForgotPasswordEmailSchema}
              onSubmit={handleSendResetEmail}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                isSubmitting,
              }) => (
                <Form style={{ width: "100%" }}>
                  <Stack spacing={2}>
                    <TextField
                      label="Email *"
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      fullWidth
                      disabled={isSubmitting}
                      variant="outlined"
                      size="medium"
                      placeholder="Enter email"
                      InputProps={{ style: { borderRadius: 10 } }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : null
                      }
                      fullWidth
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        fontWeight: 600,
                        fontSize: "1rem",
                        mt: 1,
                      }}
                    >
                      {isSubmitting ? "Sending..." : "Send Reset Code"}
                    </Button>
                  </Stack>
                </Form>
              )}
            </Formik>
          )}

          {step === 'otp' && (
            <Formik
              initialValues={otpInitialValues}
              validationSchema={OTPSchema}
              onSubmit={handleVerifyOTP}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                isSubmitting,
              }) => (
                <Form style={{ width: "100%" }}>
                  <Stack spacing={2}>
                    <TextField
                      label="OTP *"
                      name="otp"
                      value={values.otp}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.otp && Boolean(errors.otp)}
                      helperText={touched.otp && errors.otp}
                      fullWidth
                      disabled={isSubmitting}
                      variant="outlined"
                      size="medium"
                      placeholder="Enter 4-digit OTP"
                      InputProps={{ style: { borderRadius: 10 } }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : null
                      }
                      fullWidth
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        fontWeight: 600,
                        fontSize: "1rem",
                        mt: 1,
                      }}
                    >
                      {isSubmitting ? "Verifying..." : "Verify OTP"}
                    </Button>
                  </Stack>
                </Form>
              )}
            </Formik>
          )}

          {step === 'password' && (
            <Formik
              initialValues={changePasswordInitialValues}
              validationSchema={ChangePasswordSchema}
              onSubmit={handleResetPassword}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                isSubmitting,
              }) => (
                <Form style={{ width: "100%" }}>
                  <Stack spacing={2}>
                    <TextField
                      label="New Password *"
                      name="newPassword"
                      type="password"
                      value={values.newPassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.newPassword && Boolean(errors.newPassword)}
                      helperText={touched.newPassword && errors.newPassword}
                      fullWidth
                      disabled={isSubmitting}
                      variant="outlined"
                      size="medium"
                      placeholder="Enter new password"
                      InputProps={{ style: { borderRadius: 10 } }}
                    />
                    <TextField
                      label="Confirm Password *"
                      name="confirmPassword"
                      type="password"
                      value={values.confirmPassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                      helperText={touched.confirmPassword && errors.confirmPassword}
                      fullWidth
                      disabled={isSubmitting}
                      variant="outlined"
                      size="medium"
                      placeholder="Confirm new password"
                      InputProps={{ style: { borderRadius: 10 } }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : null
                      }
                      fullWidth
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        fontWeight: 600,
                        fontSize: "1rem",
                        mt: 1,
                      }}
                    >
                      {isSubmitting ? "Resetting..." : "Reset Password"}
                    </Button>
                  </Stack>
                </Form>
              )}
            </Formik>
          )}

          <Typography
            align="center"
            sx={{ mt: 2, color: "text.secondary", fontSize: "0.975rem" }}
          >
            Remember your password?{" "}
            <Button
              variant="text"
              color="primary"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.975rem",
                p: 0,
                minWidth: 0,
              }}
              onClick={() => {
                navigate("/login");
              }}
            >
              Back to Login
            </Button>
          </Typography>
        </Paper>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ mt: 2 }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ForgotPasswordForm;